<script>
    import Button, { Label } from '@smui/button';
    import Checkbox from '@smui/checkbox';
    import FormField from '@smui/form-field';
    import List, { Item } from '@smui/list';
    import Select, { Option } from '@smui/select';
    import Textfield from '@smui/textfield';
    import HelperText from '@smui/textfield/helper-text';
    import { derived } from 'svelte/store';
    import ConfirmDialog from "../common/confirm/ConfirmDialog.svelte";
    import { countryListAllIsoData } from '../common/countries';
    import ModifyColumn from '../common/multi-card-page/ModifyColumn.svelte';
    import ViewColumn from '../common/multi-card-page/ViewColumn.svelte';
    import NextButton from "../common/next-button/NextButton.svelte";
    import SaveButton from '../common/save-button/SaveButton.svelte';
    import SaveWindow from "../common/save-window/SaveWindow.svelte";
    import OrganizationChips from "./OrganizationChips.svelte";
    import OrganizationSelection from "./OrganizationSelection.svelte";
    import { isSafari } from "./browserInformation";
    import { ehLevels, idTypes, secondFactors } from "./userConstants";
    import { currentUser, userList, userStore } from "./users";
    import { usersPageState, usersSaveState, usersValidation } from "./usersState";

    let selectUserCallback, confirmDialog, selectionDialog, validSecondFactor = false;
    let firstElement

    function openDialog(organization) {
        if (userList.selectedWasUpdated()) {
            selectUserCallback = () => userList.select(organization);
            confirmDialog.open()
        } else {
            userList.select(organization)
        }
    }

    const canSelectSecondFactor = derived(
        currentUser,
        $currentUser => {
            if ($currentUser.secondFactor !== "SMS") {
                $currentUser.phoneNumber = ''
            }
            else if ($currentUser.secondFactor === "Geen") {
                $currentUser.secondFactor = "SMS"
            }
            return true
        }
    )
    let mustBeAdmin = derived(
        [currentUser, userStore],
        ([$currentUser, $userStore])  => {
            return ($userStore.length === 0 || ($userStore.length === 1 && $userStore[0].id === $currentUser.id && $currentUser.admin))
        }
    );

    function addNewUser() {
        userList.resetSelected();
        resetDirtyStatus(false);
    }

    function deleteSelectedUser() {
        userList.deleteSelected();
        resetDirtyStatus(false);
    }

    function saveSelectedUser() {
        firstElement.focus()
        if ($usersSaveState) {
            userList.upsertSelected();
            resetDirtyStatus(false);
        } else {
            resetDirtyStatus(true);
        }

    }

    function resetDirtyStatus(value) {
        initialsDirty = value;
        lastNameDirty = value;
        emailDirty = value;
        documentExpiryDateDirty = value;
        documentNumberDirty = value;
        idTypeDirty = value
        secondFactorMethodDirty = value;
        ehLevelDirty = value;
        idCountryDirty = value;
        birthDateDirty = value;
    }

    let initialsDirty = false;
    let lastNameDirty = false;
    let emailDirty = false;
    let documentExpiryDateDirty = false;
    let documentNumberDirty = false;
    let idTypeDirty = false
    let secondFactorMethodDirty = false;
    let ehLevelDirty = false;
    let idCountryDirty = false;
    let birthDateDirty = false;

</script>
<SaveWindow saveFunction={saveSelectedUser}/>
<div class="container-fluid mb-3">
    <div class="row justify-content-center">
        <ViewColumn header="Gebruikers" on:new={addNewUser}>
            <ConfirmDialog on:confirm={selectUserCallback} bind:this={confirmDialog}/>
            <List>
                {#each $userStore as user}
                    <Item on:SMUI:action={() => openDialog(user)}>
                        <div class="my-own-grid">
                            <span>{user.initial} {user.lastName}</span>
                        </div>
                    </Item>
                {/each}
            </List>
        </ViewColumn>
        <ModifyColumn header={$currentUser.id === "" ? "Nieuwe gebruiker toevoegen" : "Gebruiker bewerken"}
                      on:delete={deleteSelectedUser} bind:editing={$currentUser.id}>
            <OrganizationSelection bind:this={selectionDialog}/>
            <div class="container-fluid">
                <div class="row mt-2">
                    <div class="col-12 col-xl-4 mt-3 mt-xl-0">
                        <Textfield variant="outlined"
                                   bind:dirty={initialsDirty}
                                   invalid={initialsDirty && !$usersValidation.initialResult.valid}
                                   bind:value={$currentUser.initial}
                                   bind:this={firstElement}
                                   label="Voornamen*"/>
                    </div>
                    <div class="col-12 col-xl-4 mt-3 mt-xl-0">
                        <Textfield variant="outlined"
                                   bind:value={$currentUser.middleName}
                                   label="Tussenvoegsel"/>
                    </div>
                    <div class="col-12 col-xl-4 mt-3 mt-xl-0">
                        <Textfield style="width: 100%" variant="outlined"
                                   bind:dirty={lastNameDirty}
                                   invalid={lastNameDirty && !$usersValidation.lastNameResult.valid}
                                   bind:value={$currentUser.lastName}
                                   label="Achternaam*"/>
                    </div>

                    <div class="col-12 col-lg-6 mt-3">
                        <Textfield variant="outlined"
                                   invalid={emailDirty && !$usersValidation.emailResult.valid}
                                   bind:dirty={emailDirty}
                                   bind:value={$currentUser.email}
                                   label="Persoonlijke email*"/>
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <Textfield type="date" variant="outlined"
                                   bind:dirty={birthDateDirty}
                                   invalid={birthDateDirty && !$usersValidation.birthDateResult.valid}
                                   bind:value={$currentUser.birthDate}
                                   label="Geboortedatum*"/>
                        {#if isSafari()}
                            <HelperText>jjjj-mm-dd</HelperText>
                        {/if}
                    </div>
                </div>
                <div class="cng-card__divider mt-3"></div>
                <div class="row">
                    <div class="col-12 col-lg-6 mt-3">
                        <Select style="width: 100%" variant="outlined"
                                bind:dirty={ehLevelDirty}
                                invalid={ehLevelDirty && !$usersValidation.ehLevelResult.valid}
                                bind:value={$currentUser.ehLevel}
                                label="eH betrouwbaarheidsniveau*">
                            {#each ehLevels as ehLevel}
                                <Option value="{ehLevel}">{ehLevel}</Option>
                            {/each}
                        </Select>
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        {#if $canSelectSecondFactor}
                            <Select style="width: 100%" variant="outlined"
                                    bind:dirty={secondFactorMethodDirty}
                                    invalid={secondFactorMethodDirty && !$usersValidation.secondFactorResult.valid}
                                    bind:value={$currentUser.secondFactor}
                                    label="2e factor authenticatie*">
                                {#each secondFactors as item}
                                    <Option value="{item}">{item}</Option>
                                {/each}
                            </Select>
                        {:else}
                            <Textfield disabled variant="outlined"
                                       bind:value={$currentUser.secondFactor}
                                       label="2e factor authenticatie*"/>
                        {/if}
                        {#if $canSelectSecondFactor && $currentUser.secondFactor === "SMS"}
                            <Textfield style="width: 100%; margin-top: 40px" variant="outlined"
                                       invalid={!$usersValidation.phoneNumberResult.valid}
                                       bind:value={$currentUser.phoneNumber}
                                       label="Telefoonnummer"/>
                            {/if}
                    </div>
                    <div class="col-12 mt-3">
                        <FormField style="width: 100%">
                            <Checkbox bind:checked={$currentUser.existingAccount}/>
                            <span slot="label">Gebruiker heeft al een eHerkenningsmiddel op dit niveau</span>
                        </FormField>
                    </div>
                    <div class="col"></div>
                </div>
                <div class="cng-card__divider mt-3"></div>
                <div class="row">
                    <div class="col-12 col-lg-6 mt-3">
                        <Textfield type="date" variant="outlined"
                                   bind:dirty={documentExpiryDateDirty}
                                   invalid={documentExpiryDateDirty && !$usersValidation.documentExpiryDateResult.valid}
                                   bind:value={$currentUser.documentExpiryDate}
                                   label="Vervaldatum identiteitsbewijs*"/>
                        {#if isSafari()}
                            <HelperText>jjjj-mm-dd</HelperText>
                        {/if}
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <Select style="width: 100%" variant="outlined"
                                bind:dirty={idTypeDirty}
                                invalid={idTypeDirty && !$usersValidation.idTypeResult.valid}
                                bind:value={$currentUser.idType}
                                label="Type identiteitsbewijs*">
                            {#each idTypes as item}
                                <Option value="{item}">{item}</Option>
                            {/each}
                        </Select>
                    </div>
                </div>
                <div class="row mt-2 mb-3">
                    <div class="col-12 cng-alert-success mt-2 p-3">
                        Het identiteitsbewijs moet minimaal nog 2 maanden geldig zijn.
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <Textfield variant="outlined"
                                   bind:dirty={documentNumberDirty}
                                   invalid={documentNumberDirty && !$usersValidation.documentNumberResult.valid}
                                   bind:value={$currentUser.documentNumber}
                                   label="Documentnummer*"/>
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <Select style="width: 100%" variant="outlined"
                                bind:dirty={idCountryDirty}
                                invalid={idCountryDirty && !$usersValidation.idCountryResult.valid}
                                bind:value={$currentUser.idCountry}
                                label="Land van uitgifte van identiteitsbewijs*">
                            {#each countryListAllIsoData as country}
                                <Option value="{country.name}">{country.name}</Option>
                            {/each}
                        </Select>
                    </div>
                </div>
                <div class="cng-card__divider mt-3"></div>
                <div class="row">
                    {#if $mustBeAdmin}
                        <div class="col-12 cng-alert-success mt-2 p-3" >
                            Er moet minimaal 1 beheerder per organisatie worden aangesteld
                        </div>
                    {/if}
                    <div class="col-12 col-lg-6 mt-3">
                        <FormField style="width: 100%">
                            <Checkbox bind:checked={$currentUser.admin}/>
                            <span slot="label">Wordt een beheerder</span>
                        </FormField>
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <FormField style="width: 100%">
                            <Checkbox bind:checked={$currentUser.allOrganizations}/>
                            <span slot="label">Bevoegd voor alle vestigingen</span>
                        </FormField>
                    </div>
                    <div class="col-12 col-lg-6 mt-3">
                        <Button variant="raised" disabled={$currentUser.allOrganizations} on:click={() => selectionDialog.open()}><Label>Selecteer
                            organisatie</Label></Button>
                    </div>
                    <div class="col-12">
                        <OrganizationChips/>
                    </div>
                </div>
                <div class="mt-5">
                    <SaveButton on:save={saveSelectedUser}/>
                </div>
                <NextButton navigationLink="#/summary" canContinue={$usersPageState}/>
            </div>
        </ModifyColumn>
    </div>
</div>
